import { ReactElement, useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Flex, Text, Button, HelpIndicator, Tabs, Tab } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { useFeatureFlag } from "@jsq/flags-react";
import { Money } from "~/components/Money";
import { useAppContext } from "~/contexts/appContext";
import { BillingGeneratingModal } from "~/pages/Billing/components/BillingGeneratingModal";
import { useBillingDate } from "~/pages/Billing/contexts/BillingDateContext";
import { useBillingExport } from "~/pages/Billing/hooks/useBillingExport";
import { SPACING } from "~/styles/variables";
import { canTriggerBillingExports, hasBillingFARole } from "~/utils/userRoles";
import AsOfDateSelector from "../../components/AsOfDateSelector";
import { useEntityUpdate } from "../EntityUpdateContext";
import { useFilterSort } from "../FilterSortContext";
import { SummaryItem } from "../types";
import {
  pageHeaderWrapper,
  headerContainer,
  backButton,
  backButtonText,
  clientInfoContainer,
  clientTypeTag,
  summaryItemLabel,
  summaryItemIcon,
  tabsContainer,
} from "./styles_vanilla.css";

export type ClientViewTab = "overview" | "log";

type PageHeaderCardProps = {
  clientName: string;
  edition: string;
  summaryItems: Array<SummaryItem>;
  clientId: string;
  clientArenaShowSpinner?: boolean;
  activeTab: ClientViewTab;
  onTabChange: (tab: ClientViewTab) => void;
};

type LocationState = {
  fromBillingOverview?: boolean;
};

export const PageHeaderCard = ({
  clientName,
  edition,
  summaryItems,
  clientId,
  clientArenaShowSpinner,
  activeTab,
  onTabChange,
}: PageHeaderCardProps): ReactElement => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { filters, sorts } = useFilterSort();
  const { asOfDateString } = useBillingDate();
  const { triggerExport, isExporting, modalProps } = useBillingExport();
  const { hasActiveEntityUpdates } = useEntityUpdate();
  const isBillingEntityStatusUpdateFlagEnabled = useFeatureFlag(
    "billingEntityStatusUpdate",
    false,
  );
  const { user } = useAppContext();
  const canExport = canTriggerBillingExports(user?.roles || []);
  const hasLogTabAccess = hasBillingFARole(user?.roles || []);

  useEffect(() => {
    if (!hasLogTabAccess && activeTab === "log") {
      onTabChange("overview");
    }
  }, [hasLogTabAccess, activeTab, onTabChange]);

  const showSpinner =
    isBillingEntityStatusUpdateFlagEnabled &&
    (clientArenaShowSpinner || hasActiveEntityUpdates);

  const handleBackClick = useCallback(() => {
    if (location.state?.fromBillingOverview) {
      history.goBack();
    } else {
      history.push("/billing");
    }
  }, [history, location.state]);

  const handleExportClick = useCallback(() => {
    void triggerExport({
      exportType: "client_arena",
      clientId: clientId,
      filters,
      sorts,
      asOfDate: asOfDateString,
    });
  }, [triggerExport, clientId, filters, sorts, asOfDateString]);

  return (
    <div className={pageHeaderWrapper}>
      <Flex justifyContent="space-between" alignItems="center" className={headerContainer}>
        <Flex
          alignItems="center"
          gap={SPACING.SMALL}
          onClick={handleBackClick}
          className={backButton}
        >
          <Icon type="arrow-left" />
          <Text className={backButtonText}>Back to dashboard</Text>
        </Flex>
        <Flex alignItems="center" gap={SPACING.MEDIUM}>
          <AsOfDateSelector />
          {canExport && (
            <Button variant="neutral" onClick={handleExportClick} isLoading={isExporting}>
              Export data
            </Button>
          )}
        </Flex>
      </Flex>

      {/* Client name and type */}
      <Flex alignItems="center" gap={SPACING.MEDIUM} className={clientInfoContainer}>
        <Text variant="h1">{clientName}</Text>
        {edition && (
          <span className={clientTypeTag}>
            <Text asSpan>{edition}</Text>
          </span>
        )}
        {showSpinner && (
          <HelpIndicator
            id="client-sync-tooltip"
            placement="top"
            content="Update in progress. The EUM calculation will be refreshed once the updates have been processed."
          >
            <Icon type="sync-alt" size="small" data-testid="icon-sync-alt" />
          </HelpIndicator>
        )}
      </Flex>

      {/* Summary numbers */}
      <Flex gap={SPACING.LARGE}>
        {summaryItems.map((item, index) => (
          <Flex key={index} flexDirection="column">
            <Text>
              {item.moneyValue ? (
                <Money
                  money={item.moneyValue}
                  format={item.format}
                  dashesAsDefault
                  rounded={false}
                />
              ) : (
                item.value
              )}
            </Text>
            <Flex alignItems="center" gap={SPACING.SMALL}>
              <Text className={summaryItemLabel}>{item.label}</Text>
              <HelpIndicator
                id={`${item.label}-tooltip`}
                placement="top"
                content={item.tooltip}
              >
                <Icon
                  data-testid={`${item.label}-tooltip-icon`}
                  type="question-circle"
                  size="small"
                  className={summaryItemIcon}
                />
              </HelpIndicator>
            </Flex>
          </Flex>
        ))}
      </Flex>

      {/* Tabs */}
      <Flex className={tabsContainer}>
        <Tabs hasPaddingInline={false}>
          <Tab
            active={activeTab === "overview"}
            text="Overview"
            onClick={() => onTabChange("overview")}
            data-testid="overview-tab"
          />
          {hasLogTabAccess && (
            <Tab
              active={activeTab === "log"}
              text="Log"
              onClick={() => onTabChange("log")}
              data-testid="log-tab"
            />
          )}
        </Tabs>
      </Flex>
      <BillingGeneratingModal {...modalProps} />
    </div>
  );
};
